/**
 * Transaction Hooks
 * 
 * TanStack Query hooks for transaction operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionService } from '../services/transaction-service';
import { getCurrentUserId } from '../lib/supabase';
import type {
  Transaction,
  TransactionQueryParams,
  CreateTransactionInput,
  UpdateTransactionInput,
  SpendingByCategoryParams,
  SpendingByCategoryResponse,
  SpendingByCategoryOverTimeResponse
} from '../types/transaction.types';

/**
 * Query keys for transaction queries
 */
export const transactionKeys = {
  all: ['transactions'] as const,
  lists: () => [...transactionKeys.all, 'list'] as const,
  list: (params: TransactionQueryParams) => [...transactionKeys.lists(), params] as const,
  details: () => [...transactionKeys.all, 'detail'] as const,
  detail: (id: string) => [...transactionKeys.details(), id] as const,
  statusCounts: ['transactionStatusCounts'] as const,
  spendingByCategory: (params: SpendingByCategoryParams) => [
    ...transactionKeys.all,
    'spending-by-category',
    params,
  ] as const,
  spendingByCategoryOverTime: (params: SpendingByCategoryParams) => [
    ...transactionKeys.all,
    'spending-by-category-over-time',
    params,
  ] as const,
};

async function invalidateTransactionListAndStatusCounts(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: transactionKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: transactionKeys.statusCounts }),
  ]);
}

/**
 * Hook to list transactions with filtering and pagination
 */
export function useTransactions(params: TransactionQueryParams) {
  return useQuery({
    queryKey: transactionKeys.list(params),
    queryFn: async () => {
      const result = await transactionService.listTransactions(params);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!params.userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to get archived transactions with filtering and pagination
 */
export function useArchivedTransactions(params: TransactionQueryParams) {
  return useQuery({
    queryKey: [...transactionKeys.list(params), 'archived'],
    queryFn: async () => {
      const result = await transactionService.getArchivedTransactions(params);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!params.userId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook to get a single transaction by ID
 */
export function useTransaction(transactionId: string) {
  return useQuery({
    queryKey: transactionKeys.detail(transactionId),
    queryFn: async () => {
      const result = await transactionService.getTransactionById(transactionId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    enabled: !!transactionId,
  });
}

/**
 * Hook to create a new transaction
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTransactionInput) => {
      const result = await transactionService.createTransaction(input);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: async () => {
      await invalidateTransactionListAndStatusCounts(queryClient);
      // Also invalidate account queries since balances may have changed
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Hook to update a transaction
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, updates }: { transactionId: string; updates: UpdateTransactionInput }) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      const result = await transactionService.updateTransaction(transactionId, userId, updates);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: async (data) => {
      if (data) {
        // Invalidate the specific transaction
        await queryClient.invalidateQueries({ queryKey: transactionKeys.detail(data.id) });
        await invalidateTransactionListAndStatusCounts(queryClient);
        // Also invalidate account queries since balances may have changed
        await queryClient.invalidateQueries({ queryKey: ['accounts'] });
      }
    },
  });
}

/**
 * Hook to delete a transaction
 */
export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const result = await transactionService.deleteTransaction(transactionId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result;
    },
    onSuccess: async () => {
      await invalidateTransactionListAndStatusCounts(queryClient);
      // Also invalidate account queries since balances may have changed
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Hook to categorise a transaction
 */
export function useCategoriseTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, categoryId }: { transactionId: string; categoryId: string | null }) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      const result = await transactionService.categoriseTransaction(transactionId, userId, categoryId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: async (data) => {
      if (data) {
        // Invalidate the specific transaction
        await queryClient.invalidateQueries({ queryKey: transactionKeys.detail(data.id) });
        await invalidateTransactionListAndStatusCounts(queryClient);
      }
    },
  });
}

/**
 * Hook to bulk categorise transactions
 */
export function useBulkCategoriseTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionIds, categoryId }: { transactionIds: string[]; categoryId: string | null }) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      const result = await transactionService.bulkCategoriseTransactions(transactionIds, userId, categoryId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: async () => {
      await invalidateTransactionListAndStatusCounts(queryClient);
    },
  });
}

/**
 * Hook to bulk categorise transactions by matching descriptions with previous categorised transactions
 */
export function useBulkCategoriseByDescription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionIds }: { transactionIds: string[] }) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      const result = await transactionService.bulkCategoriseByDescription(transactionIds, userId);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: async () => {
      await invalidateTransactionListAndStatusCounts(queryClient);
    },
  });
}

/**
 * Hook to bulk delete transactions
 */
export function useBulkDeleteTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionIds: string[]) => {
      const result = await transactionService.bulkDeleteTransactions(transactionIds);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: async () => {
      await invalidateTransactionListAndStatusCounts(queryClient);
      // Also invalidate account queries since balances may have changed
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Hook to bulk update transactions
 */
export function useBulkUpdateTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionIds, updates }: { transactionIds: string[]; updates: UpdateTransactionInput }) => {
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // Update each transaction individually
      const results = await Promise.allSettled(
        transactionIds.map(transactionId => 
          transactionService.updateTransaction(transactionId, userId, updates)
        )
      );

      // Count successes and failures
      const successful: Transaction[] = [];
      const failed: Array<{ transactionId: string; error: string }> = [];

      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.data) {
          successful.push(result.value.data);
        } else {
          const error = result.status === 'rejected' 
            ? result.reason?.message || 'Unknown error'
            : result.value.error?.message || 'Unknown error';
          failed.push({ transactionId: transactionIds[index], error });
        }
      });

      return {
        successful,
        failed,
        total: transactionIds.length,
        updated: successful.length,
        failedCount: failed.length
      };
    },
    onSuccess: async () => {
      await invalidateTransactionListAndStatusCounts(queryClient);
      // Also invalidate account queries since balances may have changed
      await queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

/**
 * Hook to get aggregated spending by category
 */
export function useSpendingByCategory(params: SpendingByCategoryParams) {
  return useQuery<SpendingByCategoryResponse>({
    queryKey: transactionKeys.spendingByCategory(params),
    queryFn: async () => {
      const result = await transactionService.getSpendingByCategory(params);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    enabled: !!params.userId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get spending by category over time (time-series data)
 */
export function useSpendingByCategoryOverTime(params: SpendingByCategoryParams) {
  return useQuery<SpendingByCategoryOverTimeResponse>({
    queryKey: transactionKeys.spendingByCategoryOverTime(params),
    queryFn: async () => {
      const result = await transactionService.getSpendingByCategoryOverTime(params);
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data!;
    },
    enabled: !!params.userId && !!params.dateFrom && !!params.dateTo,
    staleTime: 5 * 60 * 1000,
  });
}

